<template>
  <div class="modal-wrapper">
    <n-modal class="bot-test-tool-modal" @cancel="emit('cancel')">
      <template v-slot:title>
        <h2>Bot Preview</h2>
      </template>
      <template v-slot:body>
        <n-loading-spinner v-if="!loaded" />
        <div class="columns is-mobile" v-if="loaded">
          <div class="column column__conversation">
            <BotPreviewMessagingClientContainer
              :user="botUser"
              :flows="selectedFlows"
              :answers="selectedAnswers"
              :currentTime="currentTime"
              @user-updated="handleBotUserUpdated"
              @channel-changed="resetContact"
            />
          </div>
          <div class="column column__cards">
            <TimeSelector v-model="currentTime" />
            <ContactDetails v-model="contact" @update:model-value="handleContactUpdated" />
            <FlowSelector
              id="flow-selector"
              v-if="isFlowSelectorVisible()"
              v-model="flowSelection"
              icon="FlowIcon"
              title="Flows"
              subtitle="Choose which flows to test"
              tooltip="Each flow can be enabled or disabled using the toggle or reordered to alter their priority. Changes made here only affect this preview tool and will not be seen by your customers."
            />
            <FlowSelector
              id="answer-selector"
              v-if="isAnswerSelectorVisible()"
              v-model="answerSelection"
              icon="AnswerIcon"
              title="FAQs"
              subtitle="Choose which FAQs to test"
              tooltip="Each FAQ can be enabled or disabled using the toggle. Changes made here only affect this preview tool and will not be seen by your customers."
              :reorderable="false"
            />
          </div>
        </div>
      </template>
    </n-modal>
  </div>
</template>

<script lang="ts" setup>
import BotPreviewMessagingClientContainer from '@/features/bots/components/botTestTool/BotPreviewMessagingClientContainer.vue';
import ContactDetails from '@/features/contacts/components/ContactDetails.vue';
import { ContactType } from '@/features/contacts/models/contactType';
import { ContactViewModel } from '@/features/contacts/models/contactViewModel';
import { BotUserViewModel } from '@/features/bots/models/botUserViewModel';
import FlowSelector from '@/features/bots/components/botTestTool/FlowSelector.vue';
import { FlowListItemViewModel } from '../../models/flowListItemViewModel';
import { LOAD_ANSWERS } from '@/store/answers/actionTypes';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { FlowSelectorItemViewModel } from '../../models/flowSelectorItemViewModel';
import { FlowStatus } from '../../models/flowStatus';
import { BOT_STUDIO_ENABLED } from '@/store/app/featureTypes';
import { AnswerListItemViewModel } from '@/features/bots/models/answers/answerListItemViewModel';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import TimeSelector from '@/features/bots/components/botTestTool/TimeSelector.vue';
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  flowToPreview: {
    type: String,
    default: '',
  },
  answerToPreview: {
    type: String,
    default: '',
  },
});

const emit = defineEmits<{
  (e: 'cancel'): void;
}>();

const botUser = ref<BotUserViewModel>({ id: '', data: {} });
const contact = ref<ContactViewModel>({
  id: '',
  type: ContactType.Lead,
  name: '',
  givenName: '',
  surname: '',
  email: '',
  phoneNumber: '',
  avatarUrl: '',
  customAttributes: {},
});
const flows = ref<FlowListItemViewModel[]>([]);
const answers = ref<AnswerListItemViewModel[]>([]);

const flowSelection = ref<FlowSelectorItemViewModel[]>([]);
const answerSelection = ref<FlowSelectorItemViewModel[]>([]);
const currentTime = ref(new Date());
const loaded = ref(false);

const store = useStore();
const isWorkspaceFeatureEnabled = (feature: string, defaultValue = false) =>
  computed(() => store.getters.isWorkspaceFeatureEnabled(feature, defaultValue));

const loadFlows = (doLoad: boolean): Promise<FlowListItemViewModel[]> => store.dispatch(LOAD_FLOWS, doLoad);
const loadAnswers = (doLoad = false): Promise<AnswerListItemViewModel[]> => store.dispatch(LOAD_ANSWERS, doLoad);

const selectedFlows = computed(() => {
  return flows.value
    .filter((flow) => flowSelection.value.find((f) => f.id === flow.id)?.isSelected)
    .map((flow) => ({
      ...flow,
      priority: flowSelection.value.find((f) => f.id === flow.id)?.priority,
    }));
});

const selectedAnswers = computed(() => {
  return answers.value.filter((flow) => answerSelection.value.find((f) => f.id === flow.id)?.isSelected);
});

onMounted(() => getFlows());

async function getFlows() {
  flows.value = await loadFlows(false);
  answers.value = await loadAnswers(false);
  enableCurrentFlowOrAnswer();
  const botStudioEnabled = isWorkspaceFeatureEnabled(BOT_STUDIO_ENABLED, true);

  flowSelection.value = flows.value
    .sort((a, b) => b.priority - a.priority)
    .map((f) => ({
      id: f.id,
      name: f.name,
      status: f.status,
      isSelected: !botStudioEnabled ? true : f.status === FlowStatus.Live,
      priority: f.priority,
    }));

  answerSelection.value = answers.value
    .sort((a1, a2) => a1.title.localeCompare(a2.title))
    .map((f) => ({
      id: f.id,
      name: f.title,
      status: f.status,
      isSelected: !botStudioEnabled ? true : f.status === FlowStatus.Live,
      priority: 1,
    }));
  loaded.value = true;
}

function handleBotUserUpdated(updateUser: BotUserViewModel) {
  Object.entries(updateUser.data).forEach(([key, val]) => applyBotUserUpdateToContact(key, val));
  contact.value = { ...contact.value }; // hack to make it reactive as nullable dates and dictionary entries not reactive
}

function handleContactUpdated() {
  updateBotUser('appUser.type', contact.value.type);
  updateBotUser('appUser.givenName', contact.value.givenName);
  updateBotUser('appUser.surname', contact.value.surname);
  updateBotUser('appUser.name', getName());
  updateBotUser('appUser.email', contact.value.email);
  updateBotUser('appUser.phoneNumber', contact.value.phoneNumber);
  updateBotUser('appUser.firstSeen', contact.value.firstSeenUtc);
  updateBotUser('appUser.lastSeen', contact.value.lastSeenUtc);
  updateBotUser('appUser.lastContacted', contact.value.lastContactedUtc);
  updateBotUser('appUser.lastHeardFrom', contact.value.lastHeardFromUtc);

  Object.entries(contact.value.customAttributes).forEach(([key, val]) => {
    updateBotUser(`appUser.customAttributes.${key}`, val);
  });
}

function resetContact() {
  contact.value = {
    id: '',
    type: ContactType.Lead,
    name: '',
    givenName: '',
    surname: '',
    email: '',
    phoneNumber: '',
    avatarUrl: '',
    firstSeenUtc: undefined,
    lastHeardFromUtc: undefined,
    lastSeenUtc: undefined,
    lastContactedUtc: undefined,
    customAttributes: {},
  };
  handleContactUpdated();
  resetCustomAttributes();
}

function isFlowSelectorVisible() {
  return flows.value.length > 0;
}

function isAnswerSelectorVisible() {
  return answers.value.length > 0;
}

function enableCurrentFlowOrAnswer() {
  if (props.flowToPreview) {
    const flow = flows.value.find((f) => f.id === props.flowToPreview);
    if (flow) {
      flow.status = FlowStatus.Live;
    }
  }

  if (props.answerToPreview) {
    const answer = answers.value.find((f) => f.id === props.answerToPreview);
    if (answer) {
      answer.status = FlowStatus.Live;
    }
  }
}

function resetCustomAttributes() {
  Object.entries(botUser.value.data).forEach(([key, val]) => {
    if (key.startsWith('appUser.customAttributes.')) {
      updateBotUser(key, undefined);
    }
  });
}

function getName() {
  let name = '';
  if (contact.value.givenName) {
    name = contact.value.givenName;
  }
  if (contact.value.surname) {
    name = `${name} ${contact.value.surname}`;
  }
  return name;
}

function applyBotUserUpdateToContact(name: string, value: any) {
  if (name.startsWith('appUser.customAttributes')) {
    const attributeName = name.substring(name.lastIndexOf('.') + 1);
    contact.value.customAttributes[attributeName] = value;
  } else if (name === 'appUser.name') {
    contact.value.name = value;
  } else if (name === 'appUser.givenName') {
    contact.value.givenName = value;
  } else if (name === 'appUser.surname') {
    contact.value.surname = value;
  } else if (name === 'appUser.email') {
    contact.value.email = value;
  } else if (name === 'appUser.phoneNumber') {
    contact.value.phoneNumber = value;
  } else if (name === 'appUser.lastSeen') {
    contact.value.lastSeenUtc = value;
  } else if (name === 'appUser.firstSeen') {
    contact.value.firstSeenUtc = value;
  } else if (name === 'appUser.lastContacted') {
    contact.value.lastContactedUtc = value;
  } else if (name === 'appUser.lastHeardFrom') {
    contact.value.lastHeardFromUtc = value;
  }
}

function updateBotUser(key: string, value: any) {
  botUser.value.data[key] = value;
}
</script>

<style lang="scss" scoped>
.modal-wrapper {
  :deep(.n-modal) {
    min-width: 900px;

    .n-modal__modal {
      height: 90vh;
      min-height: 500px;
      width: 900px;
    }

    .n-modal__header {
      font-size: 1.5625rem;
      background: #ffffff;
    }

    .n-modal__body {
      background: $color-netcall-light-grey;
      border-radius: 0 0 6px 6px;
      padding: 0;
      overflow: auto;
    }
    .n-modal__footer {
      display: none;
    }
  }
}

.columns {
  margin: 0;
  height: 100%;
}

.columns:last-child {
  margin-bottom: 0;
  height: 100%;
}

.column__conversation {
  padding: 0;
}

.column__cards {
  overflow: auto;
  padding: 16px;
  width: 424px;
  height: 100%;
  flex: none;
  border-left: 1px solid #dbdbdb; // modal headers bottom border colour

  > :not(:last-child) {
    margin-bottom: 1rem;
  }
}
</style>
